import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, Brain, Target, Briefcase, Search,
  ArrowRight, CheckCircle, Play, TrendingUp, Award,
  Zap, Shield, Star, BarChart3, Filter, Sparkles
} from 'lucide-react';

const CompaniesLandingPage = () => {
  const capabilities = [
    {
      icon: Brain,
      title: "AI Talent Matching",
      description: "Athena AI powered candidate recommendations based on skills, CTS score, and cultural fit"
    },
    {
      icon: Target,
      title: "Hiring Challenges",
      description: "Create custom coding assessments to evaluate candidates before interviews"
    },
    {
      icon: Users,
      title: "Campus Recruitment",
      description: "Direct access to 500+ colleges and their placement cells"
    },
    {
      icon: Filter,
      title: "Skill-Based Hiring",
      description: "Filter candidates by verified skills, certifications, and contest performance"
    }
  ];

  const dashboardFeatures = [
    "Smart candidate search with 20+ filters",
    "AI-generated interview questions",
    "Application pipeline management",
    "Hiring analytics dashboard",
    "Automated shortlisting emails",
    "CTS score-based ranking"
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Verified Developers",
      description: "All candidates are skill-verified through coding assessments"
    },
    {
      icon: Sparkles,
      title: "AI Candidate Ranking",
      description: "Our AI analyzes 1000s of data points to rank best matches"
    },
    {
      icon: Zap,
      title: "Faster Hiring",
      description: "Reduce time-to-hire by 60% with pre-screened candidates"
    },
    {
      icon: TrendingUp,
      title: "Campus Pipeline",
      description: "Build relationships with top colleges for early talent access"
    }
  ];

  const stats = [
    { value: "50,000+", label: "Verified Developers" },
    { value: "500+", label: "Partner Colleges" },
    { value: "200+", label: "Hiring Companies" },
    { value: "10,000+", label: "Successful Hires" }
  ];

  const testimonials = [
    {
      quote: "EvalentX helped us hire 50+ developers in just 3 months. The AI matching is incredibly accurate.",
      author: "Priya Sharma",
      role: "Head of Talent, TechCorp India"
    },
    {
      quote: "The CTS score gives us instant confidence about a candidate's abilities. It's a game-changer.",
      author: "Rahul Verma",
      role: "Engineering Manager, StartupXYZ"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/5 to-transparent" />
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-8">
              <Building2 size={18} />
              For Recruiters & Companies
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Hire India's Most Skilled Developers{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                Through EvalentX
              </span>
            </h1>
            
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Access 50,000+ verified developers with AI-powered matching, 
              skill assessments, and instant CTS score rankings.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/company/auth"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg shadow-cyan-500/25"
                data-testid="post-job-btn"
              >
                Post a Job
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/company/auth"
                className="flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-semibold hover:bg-white/10 transition-all"
                data-testid="start-hiring-btn"
              >
                Start Hiring
              </Link>
              <Link
                to="/company/auth"
                className="flex items-center gap-2 px-6 py-4 text-cyan-400 hover:text-cyan-300"
                data-testid="explore-talent-btn"
              >
                Explore Talent
                <Search size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                  {stat.value}
                </p>
                <p className="text-gray-400 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Capabilities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Intelligent Hiring Platform
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Everything you need to find, evaluate, and hire top developer talent
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((cap, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-cyan-500/30 transition-colors"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <cap.icon className="text-cyan-400" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{cap.title}</h3>
                <p className="text-gray-400 text-sm">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-20 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 shadow-2xl">
                {/* Mock Dashboard Preview */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-semibold">Candidate Search</h3>
                  <span className="text-xs text-gray-500">AI-Powered</span>
                </div>
                
                {/* Search Bar */}
                <div className="bg-white/5 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <Search className="text-gray-500" size={18} />
                  <span className="text-gray-400 text-sm">Python, Machine Learning, 500+ CTS...</span>
                </div>
                
                {/* Candidate Cards */}
                <div className="space-y-3">
                  {[
                    { name: "Arjun Kumar", cts: 842, skills: ["Python", "TensorFlow"], match: 95 },
                    { name: "Priya Patel", cts: 798, skills: ["Python", "PyTorch"], match: 89 },
                    { name: "Rahul Singh", cts: 756, skills: ["Python", "Scikit-learn"], match: 84 }
                  ].map((candidate, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                          <span className="text-cyan-400 font-bold text-sm">{candidate.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{candidate.name}</p>
                          <p className="text-gray-500 text-xs">{candidate.skills.join(", ")}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-cyan-400 font-bold">{candidate.match}%</p>
                        <p className="text-gray-500 text-xs">CTS: {candidate.cts}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Recruiter Dashboard
              </h2>
              <p className="text-gray-400 mb-8">
                Find, evaluate, and hire the best developers with our intelligent 
                recruitment platform designed for modern hiring teams.
              </p>
              
              <div className="space-y-4">
                {dashboardFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="text-cyan-400" size={14} />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/company/auth"
                className="inline-flex items-center gap-2 mt-8 text-cyan-400 hover:text-cyan-300"
              >
                Try Dashboard Demo
                <Play size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Recruiters Choose EvalentX
            </h2>
            <p className="text-gray-400">The smarter way to hire developers</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-cyan-500/30 transition-colors text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="text-cyan-400" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTS Score Explanation */}
      <section className="py-20 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm mb-6">
                <Award size={18} />
                EvalentX Talent Score
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Instant Candidate Evaluation with CTS
              </h2>
              <p className="text-gray-400 mb-8">
                The EvalentX Talent Score (CTS) is a comprehensive 0-1000 rating that combines 
                multiple factors to give you instant insights into a candidate's abilities.
              </p>
              
              <div className="space-y-4">
                {[
                  "Contest performance & rankings",
                  "Problem-solving consistency",
                  "Certifications & achievements",
                  "Hackathon participation",
                  "Code review reputation",
                  "Project portfolio quality"
                ].map((factor, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full" />
                    <span className="text-gray-300">{factor}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-32 h-32 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                  <span className="text-5xl font-bold text-white">842</span>
                  <div className="absolute -top-2 -right-2 px-2 py-1 bg-emerald-500 rounded-lg text-xs font-bold text-white">
                    Top 3%
                  </div>
                </div>
                <p className="text-gray-400">EvalentX Talent Score</p>
                <p className="text-purple-400 font-semibold">Advanced Level</p>
              </div>
              
              <div className="space-y-3">
                {[
                  { label: "Contest Performance", score: 180, max: 200, color: "bg-cyan-500" },
                  { label: "Problem Solving", score: 175, max: 200, color: "bg-purple-500" },
                  { label: "Certifications", score: 140, max: 150, color: "bg-emerald-500" },
                  { label: "Hackathons", score: 85, max: 100, color: "bg-amber-500" }
                ].map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">{item.label}</span>
                      <span className="text-white">{item.score}/{item.max}</span>
                    </div>
                    <div className="bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${item.color}`}
                        style={{ width: `${(item.score / item.max) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">What Recruiters Say</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} size={16} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4">"{t.quote}"</p>
                <div>
                  <p className="text-white font-semibold">{t.author}</p>
                  <p className="text-gray-500 text-sm">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-3xl p-12 text-center">
            <div className="inline-flex p-4 bg-cyan-500/20 rounded-2xl mb-6">
              <Briefcase className="text-cyan-400" size={40} />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Hire Top Talent?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Join 200+ companies already hiring through EvalentX. 
              Access India's most skilled and verified developers today.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/company/auth"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all"
                data-testid="cta-start-btn"
              >
                Start Hiring
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/company/auth"
                className="flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-all"
              >
                Book Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompaniesLandingPage;
